import { QueryToken, tokenizeQuery } from './tokenizer'
import {
  ParsedQuery,
  QueryComparisonNode,
  QueryComparisonOperator,
  QueryExpressionNode,
  QueryOrderByItem,
  QueryParseError,
  QueryValueNode,
} from './types'

/**
 * Recursive-descent parser for ScQL.
 *
 * Grammar:
 *   query      := orExpr? orderBy?
 *   orderBy    := ORDER BY orderItem (',' orderItem)*
 *   orderItem  := IDENT (ASC | DESC)?
 *   orExpr     := andExpr (OR andExpr)*
 *   andExpr    := notExpr (AND notExpr)*
 *   notExpr    := NOT notExpr | primary
 *   primary    := '(' orExpr ')' | comparison
 *   comparison := IDENT (op value | IN list | NOT IN list | IS [NOT] EMPTY)
 *   list       := '(' value (',' value)* ')'
 *   value      := STRING | NUMBER | DURATION | TRUE | FALSE
 *              | IDENT ['(' [value (',' value)*] ')']   -- bare word or function
 */
class Parser {
  private readonly tokens: QueryToken[]
  private pos = 0

  constructor(input: string) {
    this.tokens = tokenizeQuery(input)
  }

  parse(): ParsedQuery {
    let where: QueryExpressionNode | null = null

    if (!this.isKeyword('ORDER') && !this.check('eof')) {
      where = this.parseOr()
    }

    const orderBy = this.parseOrderBy()

    const trailing = this.peek()
    if (trailing.type !== 'eof') {
      throw new QueryParseError(
        `Unexpected '${trailing.raw}'`,
        trailing.position,
      )
    }

    return { where, orderBy }
  }

  // -- expression levels ----------------------------------------------------

  private parseOr(): QueryExpressionNode {
    const children = [this.parseAnd()]
    while (this.matchKeyword('OR')) {
      children.push(this.parseAnd())
    }
    return children.length === 1 ? children[0] : { kind: 'or', children }
  }

  private parseAnd(): QueryExpressionNode {
    const children = [this.parseNot()]
    while (this.matchKeyword('AND')) {
      children.push(this.parseNot())
    }
    return children.length === 1 ? children[0] : { kind: 'and', children }
  }

  private parseNot(): QueryExpressionNode {
    if (this.matchKeyword('NOT')) {
      return { kind: 'not', child: this.parseNot() }
    }
    return this.parsePrimary()
  }

  private parsePrimary(): QueryExpressionNode {
    if (this.match('lparen')) {
      const expr = this.parseOr()
      this.expect('rparen', `Expected ')'`)
      return expr
    }
    return this.parseComparison()
  }

  private parseComparison(): QueryComparisonNode {
    const fieldToken = this.peek()
    if (fieldToken.type !== 'ident') {
      throw new QueryParseError(
        fieldToken.type === 'eof'
          ? 'Expected a field name'
          : `Expected a field name, got '${fieldToken.raw}'`,
        fieldToken.position,
      )
    }
    this.advance()
    const field = fieldToken.value

    // IS [NOT] EMPTY / IS [NOT] NULL
    if (this.matchKeyword('IS')) {
      const negated = this.matchKeyword('NOT')
      if (this.matchKeyword('EMPTY') || this.matchKeyword('NULL')) {
        return {
          kind: 'comparison',
          field,
          operator: negated ? 'IS NOT EMPTY' : 'IS EMPTY',
          values: [],
          position: {
            start: fieldToken.position.start,
            end: this.previous().position.end,
          },
        }
      }
      const bad = this.peek()
      throw new QueryParseError(
        `Expected EMPTY after IS${negated ? ' NOT' : ''}`,
        bad.position,
      )
    }

    // [NOT] IN (a, b, c)
    const negatedIn = this.matchKeyword('NOT')
    if (this.matchKeyword('IN')) {
      this.expect('lparen', `Expected '(' after IN`)
      const values: QueryValueNode[] = [this.parseValue()]
      while (this.match('comma')) {
        values.push(this.parseValue())
      }
      this.expect('rparen', `Expected ')' to close IN list`)
      return {
        kind: 'comparison',
        field,
        operator: negatedIn ? 'NOT IN' : 'IN',
        values,
        position: {
          start: fieldToken.position.start,
          end: this.previous().position.end,
        },
      }
    }
    if (negatedIn) {
      const bad = this.peek()
      throw new QueryParseError(`Expected IN after NOT`, bad.position)
    }

    // Simple comparison
    const opToken = this.peek()
    if (opToken.type !== 'operator') {
      throw new QueryParseError(
        `Expected an operator after '${field}' (=, !=, >, >=, <, <=, ~, !~, IN, IS EMPTY)`,
        opToken.position,
      )
    }
    this.advance()
    const operator = opToken.value as QueryComparisonOperator
    const value = this.parseValue()

    return {
      kind: 'comparison',
      field,
      operator,
      values: [value],
      position: { start: fieldToken.position.start, end: value.position.end },
    }
  }

  // -- values ---------------------------------------------------------------

  private parseValue(): QueryValueNode {
    const token = this.peek()

    switch (token.type) {
      case 'string':
        this.advance()
        return { kind: 'string', value: token.value, position: token.position }
      case 'number':
        this.advance()
        return {
          kind: 'number',
          value: Number(token.value),
          position: token.position,
        }
      case 'duration':
        this.advance()
        return { kind: 'duration', raw: token.value, position: token.position }
      case 'ident': {
        this.advance()
        const upper = token.value.toUpperCase()
        if (upper === 'TRUE' || upper === 'FALSE') {
          return {
            kind: 'boolean',
            value: upper === 'TRUE',
            position: token.position,
          }
        }
        // Function call: ident followed immediately by '('
        if (this.check('lparen')) {
          this.advance()
          const args: QueryValueNode[] = []
          if (!this.check('rparen')) {
            args.push(this.parseValue())
            while (this.match('comma')) {
              args.push(this.parseValue())
            }
          }
          const close = this.expect('rparen', `Expected ')' to close function`)
          return {
            kind: 'function',
            name: token.value,
            args,
            position: { start: token.position.start, end: close.position.end },
          }
        }
        // Bare word treated as string value (e.g. status = done)
        return { kind: 'string', value: token.value, position: token.position }
      }
      default:
        throw new QueryParseError(
          token.type === 'eof'
            ? 'Expected a value'
            : `Expected a value, got '${token.raw}'`,
          token.position,
        )
    }
  }

  // -- ORDER BY -------------------------------------------------------------

  private parseOrderBy(): QueryOrderByItem[] {
    if (!this.matchKeyword('ORDER')) return []
    if (!this.matchKeyword('BY')) {
      throw new QueryParseError(`Expected BY after ORDER`, this.peek().position)
    }

    const items: QueryOrderByItem[] = []
    do {
      const fieldToken = this.peek()
      if (fieldToken.type !== 'ident') {
        throw new QueryParseError(
          'Expected a field name in ORDER BY',
          fieldToken.position,
        )
      }
      this.advance()
      let direction: 'asc' | 'desc' = 'asc'
      if (this.matchKeyword('ASC')) direction = 'asc'
      else if (this.matchKeyword('DESC')) direction = 'desc'
      items.push({ field: fieldToken.value, direction })
    } while (this.match('comma'))

    return items
  }

  // -- token helpers ----------------------------------------------------------

  private peek(): QueryToken {
    return this.tokens[this.pos]
  }

  private previous(): QueryToken {
    return this.tokens[this.pos - 1]
  }

  private advance(): QueryToken {
    const token = this.tokens[this.pos]
    if (token.type !== 'eof') this.pos++
    return token
  }

  private check(type: QueryToken['type']): boolean {
    return this.peek().type === type
  }

  private match(type: QueryToken['type']): boolean {
    if (this.check(type)) {
      this.advance()
      return true
    }
    return false
  }

  private isKeyword(keyword: string): boolean {
    const token = this.peek()
    return token.type === 'ident' && token.value.toUpperCase() === keyword
  }

  private matchKeyword(keyword: string): boolean {
    if (this.isKeyword(keyword)) {
      this.advance()
      return true
    }
    return false
  }

  private expect(type: QueryToken['type'], message: string): QueryToken {
    if (this.check(type)) {
      return this.advance()
    }
    throw new QueryParseError(message, this.peek().position)
  }
}

export function parseQuery(input: string): ParsedQuery {
  return new Parser(input).parse()
}

/** Parse without throwing — returns the error for inline display. */
export function tryParseQuery(input: string): {
  query: ParsedQuery | null
  error: QueryParseError | null
} {
  try {
    return { query: parseQuery(input), error: null }
  } catch (error) {
    if (error instanceof QueryParseError) {
      return { query: null, error }
    }
    throw error
  }
}
